// src/services/projectApi.ts
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
import { createAuthHeaders, getAuthToken } from '../utils/apiHelpers';

import {
  ProjectResponse,
  CreateProjectRequest,
  UpdateProjectRequest,
  ProjectsListResponse,
  SharePointLibrary,
  LibrariesResponse,
  AuditLogsResponse,
  AuditLogsQueryParams,
} from '../types';

// Re-export for convenience
export type { ProjectResponse };

export interface ProjectApiError {
  error: boolean;
  message: string;
  code?: 'AUTH_REQUIRED' | 'NOT_FOUND' | 'VALIDATION_ERROR' | 'NETWORK_ERROR' | 'MISSING_SITE_ID' | 'VERSION_MISMATCH' | 'INSUFFICIENT_PERMISSIONS';
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

// Create project
export async function createProject(data: CreateProjectRequest): Promise<ProjectResponse> {
  const response = await fetch(`${API_BASE_URL}/api/projects`, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as ProjectApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to create project: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectApiError;
  }

  return response.json();
}

// Get project
export async function getProject(projectId: number): Promise<ProjectResponse> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project not found', code: 'NOT_FOUND' } as ProjectApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to get project: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectApiError;
  }

  return response.json();
}

// List projects
export async function listProjects(params?: {
  status?: string;
  page?: number;
  pageSize?: number;
  projectDefinitionId?: number;
  inProgressAssignmentsOnly?: boolean;
}): Promise<ProjectsListResponse> {
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.append('status', params.status);
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params?.projectDefinitionId) queryParams.append('projectDefinitionId', params.projectDefinitionId.toString());
  if (params?.inProgressAssignmentsOnly === true) queryParams.append('inProgressAssignmentsOnly', 'true');

  const url = `${API_BASE_URL}/api/projects${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to list projects: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectApiError;
  }

  return response.json();
}

// Update project
export async function updateProject(
  projectId: number,
  data: UpdateProjectRequest
): Promise<ProjectResponse> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project not found', code: 'NOT_FOUND' } as ProjectApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as ProjectApiError;
  }

  if (response.status === 403) {
    const error = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: error.message || 'You do not have permission to update this project',
      code: 'INSUFFICIENT_PERMISSIONS',
    } as ProjectApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to update project: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectApiError;
  }

  return response.json();
}

// Delete project
export async function deleteProject(projectId: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project not found', code: 'NOT_FOUND' } as ProjectApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to delete project: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectApiError;
  }
}

// Lock project for execution
export interface ProjectLockResponse {
  projectId: number;
  status: string;
  locked: boolean;
  executionPlanCreated: boolean;
  message: string;
  actionDuplication?: {
    duplicated: number;
    actionsProcessed: number;
  };
}

export async function lockProject(projectId: number): Promise<ProjectLockResponse> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/lock`, {
    method: 'PUT',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as ProjectApiError;
  }

  if (response.status === 403) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Project cannot be locked',
      code: 'VALIDATION_ERROR',
    } as ProjectApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project not found', code: 'NOT_FOUND' } as ProjectApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to lock project: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectApiError;
  }

  return response.json();
}

// Unlock project from execution back to planning (System Admin only)
export interface ProjectUnlockResponse {
  success: boolean;
  message: string;
  projectId: number;
  name: string;
}

/**
 * Unlock a project from execution back to planning.
 * Clears Contracted/Actual dates so next lock recreates the execution plan.
 * Requires: authenticated user; backend allows only when project status is EXECUTION.
 * Exec Admin cannot unlock (403).
 */
export async function unlockProject(projectId: number): Promise<ProjectUnlockResponse> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/unlock`, {
    method: 'PUT',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Cannot unlock project',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as ProjectApiError;
  }

  if (response.status === 403) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'You do not have permission to unlock this project',
      code: 'VALIDATION_ERROR',
    } as ProjectApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project not found', code: 'NOT_FOUND' } as ProjectApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to unlock project: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectApiError;
  }

  return response.json();
}

// ============================================================================
// Project Date Management Endpoints (Action Execution Integration)
// ============================================================================

export interface ProjectDateResponse {
  projectId: number;
  projectDate: string; // ISO 8601 date
  timezone?: string; // IANA timezone identifier (e.g., "America/New_York")
}

/**
 * Get current project date
 */
export async function getProjectDate(projectId: number): Promise<ProjectDateResponse> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/project-date`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project not found', code: 'NOT_FOUND' } as ProjectApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to get project date: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectApiError;
  }

  return response.json();
}

// Get available SharePoint libraries for a project
export async function getProjectLibraries(projectId: number): Promise<SharePointLibrary[]> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/sharepoint-libraries`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project not found', code: 'NOT_FOUND' } as ProjectApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as ProjectApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to get project libraries: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectApiError;
  }

  const data: LibrariesResponse = await response.json();
  return data.libraries;
}

// Get available SharePoint libraries for a site (without projectId)
// Endpoint: GET /api/sp/libraries?siteId={siteId}
// Returns: { libraries: SharePointLibrary[] }
// Uses app-only authentication (no user token required)
export async function getSiteLibraries(siteId: string): Promise<SharePointLibrary[]> {
  const response = await fetch(`${API_BASE_URL}/api/sp/libraries?siteId=${encodeURIComponent(siteId)}`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }

  // 404 means site not found - return empty array to avoid breaking UI
  // The user can still proceed without library selection
  if (response.status === 404) {
    console.warn(`Site not found (${siteId}). Returning empty library list.`);
    return [];
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as ProjectApiError;
  }

  if (!response.ok) {
    // For other errors, return empty array to avoid breaking the UI
    // Library selection is optional, so we don't want to block project creation
    console.warn(`Failed to get site libraries (${response.status}): ${response.statusText}. Returning empty list.`);
    return [];
  }

  const data: LibrariesResponse = await response.json();
  return data.libraries;
}

// ============================================================================
// Project Planning Optimization Endpoints
// ============================================================================

/**
 * Planning Summary Response - Lightweight summary for fast initial load
 * Response from GET /api/projects/:projectId/planning/phases
 */
export interface PlanningSummaryResponse {
  projectId: number;
  projectName: string;
  projectStartDate: string; // YYYY-MM-DD
  status: string;
  phases: Array<{
    phaseId: number;
    name: string;
    description?: string | null;
    displayOrder: number;
    taskCount: number;
    actionCount: number;
    plannedStartDate: string | null;
    plannedEndDate: string | null;
    plannedDurationHours?: number;
    status: string;
  }>;
}

/**
 * Phase Details Response - Full phase details with tasks and actions
 */
export interface PhaseDetailsResponse {
  phaseId: number;
  name: string;
  description: string | null;
  displayOrder: number;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  plannedDurationHours: number;
  tasks: Array<{
    taskId: number;
    name: string;
    description: string | null;
    displayOrder: number;
    durationHours: number;
    plannedStartDate: string | null;
    plannedEndDate: string | null;
    prerequisites: number[];
    parallelExecution: boolean;
    actions: Array<{
      actionId: number;
      taskId: number;
      actionType: string;
      durationHours: number;
      duration?: number;
      name: string | null;
      description: string | null;
      notes: string | null;
      displayOrder: number;
      plannedStartDate?: string | null;
      plannedEndDate?: string | null;
      contractedStartDate?: string | null;
      contractedEndDate?: string | null;
      actualStartDate?: string | null;
      actualEndDate?: string | null;
      status?: string;
      kpiScore?: number;
      isLocked?: boolean;
      sourceActionId: number | null;
      sourceSpItemId?: number | null;
      rejectActionId?: number | null;
      requiredDocumentCount?: number | null;
      createdAt?: string;
      lastUpdated?: string;
      createdBy?: number;
    }>;
  }>;
}

/**
 * Bulk Save Request
 */
export interface BulkSaveRequest {
  updates: {
    phases?: Array<{
      phaseId: number;
      name?: string;
      description?: string;
      displayOrder?: number;
      plannedStartDate?: string;
      plannedEndDate?: string;
      prerequisitePhaseIds?: number[];
    }>;
    tasks?: Array<{
      taskId: number;
      name?: string;
      description?: string;
      displayOrder?: number;
      durationHours?: number;
      plannedStartDate?: string;
      plannedEndDate?: string;
      prerequisiteTaskIds?: number[];
      parallelExecution?: boolean;
    }>;
    actions?: Array<{
      actionId: number;
      actionType?: string;
      durationHours?: number;
      notes?: string;
      displayOrder?: number;
      sourceActionId?: number | null;
      sourceSpItemId?: number | null;
      rejectActionId?: number | null;
      requiredDocumentCount?: number | null;
    }>;
  };
}

/**
 * Bulk Save Response
 */
export interface BulkSaveResponse {
  success: boolean;
  updated: {
    phases: number;
    tasks: number;
    actions: number;
  };
  errors: Array<{
    type: 'phase' | 'task' | 'action';
    id: number;
    message: string;
  }>;
}

/**
 * Get planning phases summary (fast initial load)
 * Endpoint: GET /api/projects/:projectId/planning/phases
 */
export async function getPlanningSummary(projectId: number): Promise<PlanningSummaryResponse> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/planning/phases`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project not found', code: 'NOT_FOUND' } as ProjectApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to get planning summary: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectApiError;
  }

  return response.json();
}

/**
 * Get phase details (progressive loading)
 * Endpoint: GET /api/projects/:projectId/planning/phases/:phaseId
 */
export async function getPhaseDetails(projectId: number, phaseId: number): Promise<PhaseDetailsResponse> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/planning/phases/${phaseId}`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Phase not found', code: 'NOT_FOUND' } as ProjectApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to get phase details: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectApiError;
  }

  return response.json();
}

/**
 * Bulk save planning updates
 * Endpoint: PUT /api/projects/:projectId/planning/bulk
 */
export async function bulkSavePlanning(projectId: number, updates: BulkSaveRequest['updates']): Promise<BulkSaveResponse> {
  const url = `${API_BASE_URL}/api/projects/${projectId}/planning/bulk`;
  const requestBody = { updates };

  const response = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(requestBody),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project not found', code: 'NOT_FOUND' } as ProjectApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    console.error('[API] bulkSavePlanning: Validation error:', error);
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as ProjectApiError;
  }

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[API] bulkSavePlanning: HTTP error:', {
      status: response.status,
      statusText: response.statusText,
      body: errorText
    });
    throw {
      error: true,
      message: `Failed to bulk save: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectApiError;
  }

  const result = await response.json();
  return result;
}

// ============================================================================
// GET /plan – Single-call plan retrieval (post-recalc refresh, Phase 5)
// ============================================================================

/**
 * GET /plan response shape (backend contract).
 * Dates are YYYY-MM-DD strings; prerequisites may be phaseId or taskId.
 */
export interface ProjectPlanResponse {
  project: {
    projectId: number;
    name: string;
    projectStartDate: string;
    status: string;
  };
  phases: Array<{
    phaseId: number;
    name: string;
    description?: string | null;
    displayOrder: number;
    plannedStartDate: string | null;
    plannedEndDate: string | null;
    contractedStartDate?: string | null;
    contractedEndDate?: string | null;
    plannedDurationHours: number;
    prerequisites: Array<{ phaseId?: number; taskId?: number }>;
    tasks: Array<{
      taskId: number;
      name: string;
      description?: string | null;
      displayOrder: number;
      plannedStartDate: string | null;
      plannedEndDate: string | null;
      contractedStartDate?: string | null;
      contractedEndDate?: string | null;
      plannedDurationHours: number;
      prerequisites: Array<{ phaseId?: number; taskId?: number }>;
      assignedUsers?: string[];
      actions: Array<{
        actionId: number;
        actionType: string;
        displayOrder: number;
        plannedStartDate: string | null;
        plannedEndDate: string | null;
        contractedStartDate?: string | null;
        contractedEndDate?: string | null;
        durationHours: number;
        notes?: string | null;
        sourceActionId: number | null;
        rejectActionId?: number | null;
        /** Backend may return emails as objects { email } or as strings */
        assignedTo?: Array<{ email: string } | string>;
      }>;
    }>;
  }>;
}

/**
 * Get full project plan in one call (GET /api/projects/:projectId/plan).
 * Use after POST recalculate-dates to refresh plan without N×getPhaseDetails.
 */
export async function getProjectPlan(projectId: number): Promise<ProjectPlanResponse> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/plan`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }

  if (response.status === 403) {
    const error = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: error.message || 'Insufficient permissions',
      code: 'VALIDATION_ERROR',
    } as ProjectApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project not found', code: 'NOT_FOUND' } as ProjectApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to get project plan: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectApiError;
  }

  return response.json();
}

// ============================================================================
// Recalculate dates
// ============================================================================

/**
 * Progress payload during recalc (Technical Design §5.3).
 * Emitted by backend when progress is requested (?progress=1 or body progress: true).
 */
export interface ProgressPayload {
  totalPhases: number;
  completedPhases: number;
  totalTasks: number;
  completedTasks: number;
  totalActions: number;
  completedActions: number;
  round?: number;
  statusMessage: string;
}

/**
 * Recalculate project dates using backend calculation
 * Endpoint: POST /api/projects/:projectId/recalculate-dates
 * Requires: System Admin role
 * Only works for projects in PLANNING status
 * Phase 6: Response may include optional project/phases (same shape as GET /plan); use when present to skip GET /plan.
 * Phase 7: When options.progress is true, request includes ?progress=1 and response includes progressLog (array of ProgressPayload).
 */
/** One validation error from lock-style validation (e.g. REVISE missing source). */
export interface RecalculateValidationError {
  code: string;
  message: string;
  actions?: Array<{ actionId: number; taskName: string }>;
}

export interface RecalculateDatesResponse {
  success: boolean;
  message: string;
  projectId?: number;
  /** When present, same shape as GET /plan; use to update plan state and skip getProjectPlan. */
  project?: ProjectPlanResponse['project'];
  phases?: ProjectPlanResponse['phases'];
  /** When progress was requested, array of progress events (last item = final status). */
  progressLog?: ProgressPayload[];
  /** When lock-style validation failed before recalc; dates were still recalculated but may be inaccurate. */
  validationErrors?: RecalculateValidationError[];
  datesMayBeInaccurate?: boolean;
}

export async function recalculateProjectDates(
  projectId: number,
  options?: { progress?: boolean }
): Promise<RecalculateDatesResponse> {
  const progress = options?.progress === true;
  const url = `${API_BASE_URL}/api/projects/${projectId}/recalculate-dates${progress ? '?progress=1' : ''}`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }

  if (response.status === 403) {
    const error = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: error.message || 'Insufficient permissions. System Admin role required.',
      code: 'VALIDATION_ERROR',
    } as ProjectApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project not found', code: 'NOT_FOUND' } as ProjectApiError;
  }

  if (response.status === 400) {
    const error = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: error.message || 'Validation failed. Project must be in PLANNING status.',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as ProjectApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to recalculate project dates: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectApiError;
  }

  return response.json();
}

/**
 * Get project lock status
 * Endpoint: GET /api/projects/:projectId/lock-status
 */
export interface ProjectLockStatusResponse {
  isLocked: boolean;
  status: string;
  message?: string;
}

export async function getProjectLockStatus(projectId: number): Promise<ProjectLockStatusResponse> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/lock-status`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project not found', code: 'NOT_FOUND' } as ProjectApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to get lock status: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectApiError;
  }

  return response.json();
}

/**
 * Get execution KPI audit logs for a project.
 * Endpoint: GET /api/projects/:projectId/audit-logs
 * Query params: actorType, kpiImpact, from, to, entityType, entityId, limit, offset.
 * Display only what the API returns; do not compute attribution in the frontend.
 */
export async function getProjectAuditLogs(
  projectId: number,
  params: AuditLogsQueryParams = {}
): Promise<AuditLogsResponse> {
  const searchParams = new URLSearchParams();
  if (params.actorType) searchParams.set('actorType', params.actorType);
  if (params.kpiImpact) searchParams.set('kpiImpact', params.kpiImpact);
  if (params.from) searchParams.set('from', params.from);
  if (params.to) searchParams.set('to', params.to);
  if (params.entityType) searchParams.set('entityType', params.entityType);
  if (params.entityId != null) searchParams.set('entityId', String(params.entityId));
  if (params.limit != null) searchParams.set('limit', String(params.limit));
  if (params.offset != null) searchParams.set('offset', String(params.offset));

  const query = searchParams.toString();
  const url = `${API_BASE_URL}/api/projects/${projectId}/audit-logs${query ? `?${query}` : ''}`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project not found', code: 'NOT_FOUND' } as ProjectApiError;
  }

  if (response.status === 400) {
    const error = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: error.message || 'Invalid parameters',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as ProjectApiError;
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: error.message || `Failed to load audit logs: ${response.statusText}`,
      code: (error as { code?: string }).code === 'GET_AUDIT_LOGS_FAILED' ? 'GET_AUDIT_LOGS_FAILED' : 'NETWORK_ERROR',
    } as ProjectApiError;
  }

  return response.json();
}

/**
 * Project Audit Report (Official or Complete view). System Admin, Exec Admin, Airport Manager only.
 * Endpoint: GET /api/projects/:projectId/audit-report?view=official|complete
 */
export type ProjectAuditReportView = 'official' | 'complete';

export interface ReportSummary {
  totalPhases: number;
  totalTasks: number;
  totalActions: number;
  completedActions: number;
  overdueActions: number;
  totalChangeOrders: number;
  totalRejections: number;
  scheduleDeltaDays: number | null;
}

export interface ReportCompletedBy {
  displayName: string;
  email: string | null;
  completedAt: string; // ISO datetime string (with time)
}

export interface ReportAssignedTo {
  displayName: string;
  email: string | null;
}

export interface ReportApprovalHistoryEntry {
  result: string;
  actorEmail: string;
  occurredUtc: string;
  comment: string | null;
  documentName: string | null;
}

export interface ReportSourceDocument {
  spItemId: number;
  documentTitle?: string;
  documentName?: string;
}

export interface ReportDocumentComment {
  spItemId: number;
  documentTitle?: string;
  documentName?: string;
  versionedComments: Array<{ source: string; commentText: string; commentedAt: string | null; versionLabel?: string }>;
}

export interface ReportAction {
  actionId: number;
  actionType: string;
  name: string | null;
  description: string | null;
  status: string;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  contractedStartDate: string | null;
  contractedEndDate: string | null;
  actualStartDate: string | null;
  actualEndDate: string | null;
  completedBy: ReportCompletedBy | null;
  assignedTo: ReportAssignedTo[];
  approvalHistory: ReportApprovalHistoryEntry[];
  sourceDocuments: ReportSourceDocument[];
  documentComments: ReportDocumentComment[];
  completionComment: string | null;
}

export interface ReportTask {
  taskId: number;
  name: string;
  description?: string;
  status: string;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  contractedStartDate: string | null;
  contractedEndDate: string | null;
  actualStartDate: string | null;
  actualEndDate: string | null;
  actions: ReportAction[];
}

export interface ReportPhase {
  phaseId: number;
  name: string;
  description?: string;
  status: string;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  contractedStartDate: string | null;
  contractedEndDate: string | null;
  actualStartDate: string | null;
  actualEndDate: string | null;
  tasks: ReportTask[];
}

export interface ReportRejection {
  logId: string;
  timestampUtc: string;
  entityType: string;
  entityId: number | null;
  projectDate: string | null;
  actorDisplayName: string | null;
  actorEmail: string | null;
  notes: unknown;
}

export interface ReportChangeOrder {
  changeOrderId: number;
  changeOrderNumber: string;
  title: string | null;
  summary: string | null;
  status: string;
  requestedBy: string | null;
  requestedDate: string;
  submittedOnUtc?: string | null;
  approvedBy: string | null;
  approvedDate: string | null;
  fromVersion: number;
  toVersion: number;
  scheduleDeltaDays: number | null;
}

export interface ReportTimelineEvent {
  type:
    | 'project_locked'
    | 'project_completed'
    | 'rejection'
    | 'change_order_committed'
    | 'change_order_rejected'
    | 'approval'
    | 'action_completed';
  timestampUtc: string | null;
  entityType?: string;
  entityId?: number;
  actorDisplayName?: string | null;
  actorEmail?: string | null;
  details?: string | null;
  projectDate?: string;
  changeOrderNumber?: string;
  title?: string | null;
  status?: string;
  requestedBy?: string | null;
  approvedBy?: string | null;
  fromVersion?: number;
  toVersion?: number;
  scheduleDeltaDays?: number | null;
  phaseName?: string;
  taskName?: string;
  actionName?: string | null;
  actionType?: string;
  actionId?: number;
  planId?: string | null;
  sourceDocPlanId?: string | null;
  rejectActionPlanId?: string | null;
  comment?: string | null;
  documentName?: string | null;
  result?: string;
  completedByDisplayName?: string;
  completedByEmail?: string | null;
}

export interface ReportDocumentVersionComment {
  source: string;
  commentText: string;
  commentedAt: string | null;
  versionLabel?: string;
}

export interface ReportDocumentVersion {
  spItemId: number;
  name: string;
  path?: string;
  lastModifiedUtc?: string;
  comments?: ReportDocumentVersionComment[];
}

export interface ProjectAuditReportResponse {
  view: 'official' | 'complete';
  projectId: number;
  projectName: string;
  status: string;
  projectStartDate: string | null;
  projectEndDate: string | null;
  projectLockedUtc?: string | null;
  generatedAt: string;
  summary: ReportSummary;
  phases: ReportPhase[];
  rejections?: ReportRejection[];
  changeOrders?: ReportChangeOrder[];
  timelineEvents?: ReportTimelineEvent[];
  documentVersions?: ReportDocumentVersion[];
}

export async function getProjectAuditReport(
  projectId: number,
  view: ProjectAuditReportView = 'official'
): Promise<ProjectAuditReportResponse> {
  const url = `${API_BASE_URL}/api/projects/${projectId}/audit-report?view=${encodeURIComponent(view)}`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project not found', code: 'NOT_FOUND' } as ProjectApiError;
  }

  if (response.status === 400) {
    const error = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: (error as { message?: string }).message || 'Invalid parameters',
      code: 'VALIDATION_ERROR',
    } as ProjectApiError;
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: (error as { message?: string }).message || 'Failed to load audit report',
      code: 'NETWORK_ERROR',
    } as ProjectApiError;
  }

  return response.json();
}

/**
 * Validate project plan
 * Endpoint: POST /api/projects/:projectId/validate
 */
export interface ProjectValidationResponse {
  valid: boolean;
  errors?: Array<{
    type: string;
    id: number;
    error: string;
    message?: string;
  }>;
  message?: string;
  code?: string;
  details?: {
    entityType?: string;
    entityName?: string;
    entityId?: number;
    reason?: string;
    cyclePath?: string[];
  };
}

export async function validateProjectPlan(projectId: number, projectPlan: any): Promise<ProjectValidationResponse> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/validate`, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(projectPlan),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project not found', code: 'NOT_FOUND' } as ProjectApiError;
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    return {
      valid: false,
      errors: error.errors || [],
      message: error.message || 'Validation failed',
      code: error.code || 'VALIDATION_ERROR',
      details: error.details,
    };
  }

  return response.json();
}

// ---------------------------------------------------------------------------
// Excel Import/Export (Planning Phase) – EIE-F1, EIE-F2, EIE-F3
// ---------------------------------------------------------------------------

/**
 * Download blank Excel template.
 * GET /api/projects/excel-template
 */
export async function downloadBlankExcelTemplate(): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/projects/excel-template`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: err.message || 'Failed to download template',
      code: 'NETWORK_ERROR',
    } as ProjectApiError;
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  const filename =
    disposition?.match(/filename="?([^";]+)"?/)?.[1]?.trim() || 'ControlTower_Project_Template.xlsx';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Download seeded Excel template for an existing PLANNING project.
 * GET /api/projects/:projectId/excel-template
 */
export async function downloadSeededExcelTemplate(projectId: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/excel-template`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }
  if (response.status === 404) {
    throw { error: true, message: 'Project not found', code: 'NOT_FOUND' } as ProjectApiError;
  }
  if (response.status === 400) {
    const err = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: err.message || 'Export only available for projects in PLANNING status',
      code: 'VALIDATION_ERROR',
    } as ProjectApiError;
  }
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: err.message || 'Failed to download template',
      code: 'NETWORK_ERROR',
    } as ProjectApiError;
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  const filename =
    disposition?.match(/filename="?([^";]+)"?/)?.[1]?.trim() || 'ControlTower_Project_Export.xlsx';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Response from GET /api/projects/:projectId/change-orders */
export interface ChangeOrderListResponse {
  changeOrders: Array<{
    changeOrderId: number;
    changeOrderNumber: string;
    title: string | null;
    summary: string | null;
    status: string;
    fromVersion: number;
    toVersion: number;
    requestedBy: number;
    requestedDate: string | null;
    submittedOn: string | null;
    submittedByDisplayName: string | null;
    submittedByEmail: string | null;
  }>;
}

/**
 * List all change orders for a project (completed/history).
 * GET /api/projects/:projectId/change-orders
 */
export async function getProjectChangeOrders(projectId: number): Promise<ChangeOrderListResponse> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/change-orders`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }
  if (response.status === 404) {
    throw { error: true, message: 'Project not found', code: 'NOT_FOUND' } as ProjectApiError;
  }
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: err.message || 'Failed to load change orders',
      code: 'NETWORK_ERROR',
    } as ProjectApiError;
  }
  return response.json();
}

/**
 * Download Change Order spreadsheet for an EXECUTION project.
 * GET /api/projects/:projectId/change-order/export
 */
export async function downloadChangeOrderExport(projectId: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/change-order/export`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }
  if (response.status === 404) {
    throw { error: true, message: 'Project not found', code: 'NOT_FOUND' } as ProjectApiError;
  }
  if (response.status === 400) {
    const err = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: err.message || 'Change Order export only available for EXECUTION projects',
      code: 'VALIDATION_ERROR',
    } as ProjectApiError;
  }
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: err.message || 'Failed to download Change Order export',
      code: 'NETWORK_ERROR',
    } as ProjectApiError;
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  const filename =
    disposition?.match(/filename="?([^";]+)"?/)?.[1]?.trim() || 'ChangeOrder_Export.xlsx';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export interface ChangeOrderValidateResponse {
  valid: boolean;
  error?: boolean;
  impactPreview?: {
    recordsAdded: Array<{ rowIndex?: number; recordType?: string; recordId?: number; name?: string }>;
    recordsEdited: Array<{ recordType?: string; recordId?: number; name?: string }>;
    recordsDeleted: Array<{ recordType?: string; recordId?: number; childCount?: number | null }>;
    prerequisitesModified?: boolean;
    assignmentChanges?: boolean;
    newProjectedFinishDate?: string | null;
    scheduleDeltaDays?: number | null;
  };
}

export interface ChangeOrderCommitResponse {
  changeOrderId: number;
  fromVersion: number;
  toVersion: number;
  changeOrderNumber: string;
  message: string;
}

/**
 * Validate Change Order spreadsheet. POST /api/projects/:projectId/change-order/validate (multipart).
 */
export async function validateChangeOrder(projectId: number, file: File): Promise<ChangeOrderValidateResponse> {
  const formData = new FormData();
  formData.append('file', file);
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/change-order/validate`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: formData,
  });

  const data = await response.json().catch(() => ({}));
  if (response.status === 400) {
    return { valid: false, error: true, ...data };
  }
  if (!response.ok) {
    throw {
      error: true,
      message: data.message || 'Validation request failed',
      code: 'NETWORK_ERROR',
    } as ProjectApiError;
  }
  return data as ChangeOrderValidateResponse;
}

/**
 * Commit Change Order. POST /api/projects/:projectId/change-order/commit (multipart + title, summary, notifyUsers).
 */
export async function commitChangeOrder(
  projectId: number,
  file: File,
  payload: { title: string; summary: string; notifyUsers: boolean }
): Promise<ChangeOrderCommitResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('title', payload.title);
  formData.append('summary', payload.summary);
  formData.append('notifyUsers', payload.notifyUsers ? 'true' : 'false');
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/change-order/commit`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: formData,
  });

  const data = await response.json().catch(() => ({}));
  if (response.status === 409) {
    throw { error: true, message: data.message || 'Project version changed', code: 'VERSION_MISMATCH' } as ProjectApiError;
  }
  if (response.status === 400) {
    throw {
      error: true,
      message: data.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: data.errors,
    } as ProjectApiError;
  }
  if (!response.ok) {
    throw {
      error: true,
      message: data.message || 'Commit failed',
      code: 'NETWORK_ERROR',
    } as ProjectApiError;
  }
  return data as ChangeOrderCommitResponse;
}

export interface ExcelImportError {
  row: number;
  column: string;
  message: string;
  code?: string;
}

export interface ExcelImportResult {
  valid: boolean;
  projectId?: number;
  replaced?: boolean;
  message?: string;
  errors?: ExcelImportError[];
  /** Warnings when import succeeded but e.g. action rows had empty Name/Description */
  warnings?: ExcelImportError[];
}

/**
 * Upload Excel file for validation and create or replace project.
 * POST /api/projects/excel-import (multipart).
 * On success backend streams NDJSON (progress + result). On validation failure returns 400 JSON.
 */
export async function excelImport(
  formData: FormData,
  onProgress?: (message: string) => void
): Promise<ExcelImportResult> {
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  // Do not set Content-Type; browser sets multipart boundary

  const response = await fetch(`${API_BASE_URL}/api/projects/excel-import`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: formData,
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectApiError;
  }

  if (response.status === 400) {
    const data = await response.json().catch(() => ({}));
    return {
      valid: false,
      errors: data.errors || [{ row: 0, column: 'Request', message: data.message || 'Validation failed', code: data.code }],
    };
  }

  if (response.status === 404 || response.status === 500) {
    const data = await response.json().catch(() => ({}));
    return {
      valid: false,
      errors: [{ row: 0, column: 'Request', message: data.message || response.statusText, code: data.code }],
    };
  }

  // For 200 with a body, consume as NDJSON stream (progress + result). Proxies may alter Content-Type.
  if (response.ok && response.body) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let resultPayload: ExcelImportResult | null = null;

    const processLine = (line: string) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      try {
        const obj = JSON.parse(trimmed);
        if (obj.type === 'progress' && typeof obj.message === 'string' && onProgress) {
          onProgress(obj.message);
        }
        if (obj.type === 'result') {
          resultPayload = {
            valid: obj.valid === true,
            projectId: obj.projectId,
            replaced: obj.replaced,
            message: obj.message,
            errors: obj.errors,
            warnings: obj.warnings,
          };
        }
      } catch {
        // ignore non-JSON lines
      }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) processLine(line);
    }
    const trimmed = buffer.trim();
    if (trimmed) processLine(trimmed);

    if (resultPayload) return resultPayload;

    return {
      valid: false,
      errors: [{
        row: 0,
        column: 'Connection',
        message: 'The response was incomplete (connection may have been reset). The project may have been created—check your project list or Planning page.',
        code: 'INCOMPLETE_RESPONSE',
      }],
    };
  }

  const fallback = await response.json().catch(() => ({}));
  return {
    valid: fallback.valid === true,
    projectId: fallback.projectId,
    replaced: fallback.replaced,
    message: fallback.message,
    errors: fallback.errors,
    warnings: fallback.warnings,
  };
}

